/**
 * assistClient.js — Unified VUKZ Assist client.
 *
 * ALL requests go through the orchestrator:  /api/vukz/orchestrator/*
 * The orchestrator resolves role → correct agent proxy internally,
 * preserving all rich context injection (wellness, PBs, training, etc.).
 *
 * Frontend no longer needs to know per-role paths — just send `role` in the body.
 *
 * Usage:
 *   import { sendAssistMessage, createAssistThread } from './assistClient';
 *   const { threadId, response } = await sendAssistMessage({ message, role: 'ATLETA' });
 */

import { AZURE } from '../../services/api';

/** Single base path — the orchestrator handles routing. */
const ORCHESTRATOR_BASE = '/vukz/orchestrator';

/**
 * Create a new conversation thread.
 * The orchestrator delegates to the role-specific proxy.
 * @param {string} [role='ATLETA'] — ATLETA | TREINADOR | PREPARADOR_FISICO | GESTOR
 * @param {number} [userId] — User ID for persistent threads
 * @param {string} [sport='swimming'] — Sport context
 * @param {number} [clubId] — Club ID (required for GESTOR role scope)
 * @returns {Promise<{ threadId: string, persistent: boolean }>}
 */
export const createAssistThread = async (role = 'ATLETA', userId = null, sport = 'swimming', clubId = null) => {
  const payload = { role };
  if (userId) payload.user_id = userId;
  if (sport) payload.sport = sport;
  if (clubId) payload.club_id = clubId;
  const { data } = await AZURE.post(`${ORCHESTRATOR_BASE}/threads`, payload);
  if (!data.success) throw new Error(data.error || 'Failed to create thread');
  return { threadId: data.data.thread_id, persistent: data.data.persistent || false };
};

/**
 * Reset conversation — creates a new Foundry thread and replaces the old one in DB.
 * @param {string} [role='ATLETA'] — Role for endpoint routing
 * @param {number} userId — User ID (required)
 * @param {string} [sport='swimming'] — Sport context
 * @param {number} [clubId] — Club ID (required for GESTOR role scope)
 * @returns {Promise<{ threadId: string, message: string }>}
 */
export const resetAssistThread = async (role = 'ATLETA', userId, sport = 'swimming', clubId = null) => {
  const payload = { role, user_id: userId, sport };
  if (clubId) payload.club_id = clubId;
  const { data } = await AZURE.post(`${ORCHESTRATOR_BASE}/threads/reset`, payload);
  if (!data.success) throw new Error(data.error || 'Failed to reset thread');
  return { threadId: data.data.thread_id, message: data.data.message };
};

/**
 * Send a message to the user's VUKZ Assist agent.
 * The orchestrator resolves role → correct agent proxy (with full context injection).
 *
 * @param {Object} opts
 * @param {string} opts.message       — User message text
 * @param {string} [opts.threadId]    — Existing thread ID (auto-created if omitted)
 * @param {string} [opts.role]        — ATLETA | TREINADOR | PREPARADOR_FISICO | GESTOR
 * @param {number} [opts.userId]      — User ID for auditing
 * @param {number} [opts.clubId]      — Club ID (scope for GESTOR / PREPARADOR_FISICO)
 * @param {string} [opts.sport]       — Sport context (default: swimming)
 * @param {string} [opts.currentRoute] — Current frontend route (for ACTION context)
 * @param {Object} [opts.executionContext] — Execution canvas state (execution_id, pending items, etc.)
 *
 * @returns {Promise<{
 *   threadId: string,
 *   response: { type: 'CHAT'|'ACTION'|'ASSIST', content?: string, action?: string,
 *               payload?: object, message?: string,
 *               actions?: Array<{action: string, payload: object}> },
 *   meta: { response_time_ms: number, run_status: string }
 * }>}
 */
export const sendAssistMessage = async ({
  message,
  threadId = null,
  role = 'ATLETA',
  userId = null,
  clubId = null,
  sport = 'swimming',
  currentRoute = '',
  executionContext = null,
  formContext = null,
  uiReferenceContext = null,
  clientMessageId = null,
  inputSource = null,
}) => {
  const payload = {
    message,
    thread_id: threadId,
    role,
    user_id: userId,
    sport,
    current_route: currentRoute,
  };

  // Telemetria Audio v1: origem do input ('text'|'audio'). O backend NUNCA
  // muda comportamento por isso — só medição (reparo de STT, erros por slot).
  if (inputSource) payload.input_source = inputSource;

  // Idempotência: 1 ação humana = 1 client_message_id. Gerado UMA vez no
  // submit (nunca no interceptor) — retries do axios reusam o mesmo config,
  // então o mesmo UUID chega ao backend, que reusa thread/run/resposta.
  if (clientMessageId) payload.client_message_id = clientMessageId;

  // Include club_id for club-scoped roles (Gestor, Prep Físico)
  if (clubId) payload.club_id = clubId;

  // Include execution context when on execution canvas
  if (executionContext) payload.execution_context = executionContext;

  // FILL_FORM v1: form state do formulário aberto — o agente responde com
  // action=fill_form (patch sugerido), nunca salva diretamente.
  if (formContext) payload.form_context = formContext;

  // C4d-2 UI_REFERENCE_CONTEXT (raw): surface + referências candidatas da
  // tela atual — HINT semântico. O backend re-resolve e autoriza (SEC-A);
  // com a flag OFF no backend o campo é ignorado (prompt byte-igual).
  if (uiReferenceContext) payload.ui_reference_context = uiReferenceContext;

  const { data } = await AZURE.post(`${ORCHESTRATOR_BASE}/chat`, payload);

  if (!data.success) {
    throw new Error(data.error || data.message || 'Erro ao comunicar com o VUKZ Assist');
  }

  return {
    threadId: data.data.thread_id,
    persistent: data.data.persistent || false,
    response: data.data.response,
    meta: data.data.meta || {},
    // CANVAS-ENGINE-1: workspace ativo (null = nenhum; undefined = backend sem flag)
    workspace: data.data.workspace,
  };
};

/**
 * Check orchestrator health (includes all agent statuses).
 * @returns {Promise<Object>} Health status with all agents
 */
export const checkAssistHealth = async () => {
  const { data } = await AZURE.get(`${ORCHESTRATOR_BASE}/health`);
  return data;
};

/**
 * LOAD_TARGET_CONTEXT (26/08): contexto compacto do target selecionado
 * (pós request_selection). O resultado vai em form_context.target_context
 * — o agente preenche baseado em dados do sistema. Read-only, fail-soft.
 *
 * @param {string} workflow — CREATE_TRAINING | CREATE_PHYSICAL_TRAINING | CREATE_NUTRITION_PLAN
 * @param {{type: string, id: number}} target
 * @returns {Promise<Object|null>} { target, context } ou null em erro
 */
export const fetchTargetContext = async (workflow, target) => {
  try {
    const { data } = await AZURE.post(`${ORCHESTRATOR_BASE}/target-context`, { workflow, target });
    return data?.success ? data.data : null;
  } catch (e) {
    console.warn('[assistClient] target-context indisponível:', e?.message);
    return null;
  }
};

/**
 * List all available agents and their capabilities.
 * Useful for displaying agent status in UI or debugging.
 * @returns {Promise<{ agents: Array }>}
 */
export const listAssistAgents = async () => {
  const { data } = await AZURE.get(`${ORCHESTRATOR_BASE}/agents`);
  return data;
};

/**
 * Submit feedback (thumbs up/down) for an agent response.
 * @param {Object} opts
 * @param {string} opts.agentName — e.g. 'coach_assist', 'athlete_assist'
 * @param {string} opts.feedbackType — 'positive' | 'negative'
 * @param {string} [opts.threadId] — Thread ID
 * @param {number} [opts.messageIndex] — Position in thread
 * @param {string} [opts.feedbackText] — Optional reason
 * @param {number} [opts.userId]
 * @param {string} [opts.role]
 * @param {number} [opts.clubId]
 * @returns {Promise<{ success: boolean }>}
 */
export const submitAssistFeedback = async ({
  agentName,
  feedbackType,
  threadId = null,
  messageIndex = null,
  feedbackText = null,
  userId = null,
  role = null,
  clubId = null,
}) => {
  const { data } = await AZURE.post(`${ORCHESTRATOR_BASE}/feedback`, {
    agent_name: agentName,
    feedback_type: feedbackType,
    thread_id: threadId,
    message_index: messageIndex,
    feedback_text: feedbackText,
    user_id: userId,
    role: role,
    club_id: clubId,
  });
  return data;
};

/**
 * Audio Input v1 — transcreve um blob de áudio em TEXTO (pt-BR).
 * Áudio é somente outro input transport: o texto retornado segue o
 * MESMO pipeline do texto digitado (handleSubmit → sendAssistMessage).
 * O áudio morre no backend — nunca vai ao agente.
 * @param {Blob} blob — áudio curto (webm/opus, mp4 etc.)
 * @returns {Promise<string>} transcript
 */
export const transcribeAudio = async (blob) => {
  const fd = new FormData();
  const ext = (blob.type || 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
  fd.append('audio', blob, `utterance.${ext}`);
  const { data } = await AZURE.post('/vukz/assist/transcribe', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
  if (!data.success || !data.text) {
    throw new Error(data.message || 'Falha na transcrição');
  }
  return data.text;
};

// ── Realtime (WebRTC) — RT2 ─────────────────────────────────────────────
// Realtime = autoridade da CONVERSA. O backend minta um ephemeral secret
// (ek_) e o browser negocia SDP direto com o Azure. A key real nunca sai
// do backend. Toda pergunta de negócio vira 1 tool call → /realtime/turn.

// RT2 passo 0 — GATE DE AMBIENTE. O Realtime só habilita se o PAR
//   (host da API configurada, environment declarado pelo backend em /realtime/health)
// for EXATAMENTE um dos alvos abaixo. Fail-closed: host desconhecido, env
// desconhecido ou combinação cruzada (host staging + env production, e
// vice-versa) → recusa. Sem '*', sem bypass por NODE_ENV.
//
// VOICE-PROD-001 (02/10/2026): prod liberado como par explícito. A segurança
// real segue no backend: ASSIST_REALTIME_ENABLED + role + USER_ALLOWLIST —
// abrir o host aqui NÃO abre voz para todos os usuários.
export const REALTIME_PILOT_TARGETS = Object.freeze(new Set([
  'api-arenastats-staging-dwddf3bbb5b3d7au.brazilsouth-01.azurewebsites.net|staging',
  'api-arenastats-d5bnakdcdbb8b5gw.brazilsouth-01.azurewebsites.net|production',
  'localhost|development',
  '127.0.0.1|development',
]));

export const getApiHost = () => {
  try { return new URL(AZURE.defaults.baseURL, window.location.origin).hostname; } catch { return ''; }
};

/** Host conhecido em ALGUM par (pré-check antes de gastar 1 request). */
export const isRealtimePilotHost = (host = getApiHost()) =>
  [...REALTIME_PILOT_TARGETS].some((t) => t.split('|')[0] === host);

/** Par exato (host, environment) autorizado. */
export const isRealtimePilotTarget = (host, environment) =>
  !!host && !!environment && REALTIME_PILOT_TARGETS.has(`${host}|${environment}`);

/**
 * Health do Realtime (flag + config + ambiente). Fail-soft.
 * `enabled` já vem GATED: false se o par (host, environment) não for alvo.
 * @returns {Promise<{enabled: boolean, environment: string|null, gate: string}>}
 */
export const checkRealtimeHealth = async () => {
  const host = getApiHost();
  if (!isRealtimePilotHost(host)) {
    return { enabled: false, environment: null, gate: `host_not_pilot:${host}` };
  }
  try {
    const { data } = await AZURE.get('/vukz/assist/realtime/health');
    const environment = data?.environment ?? data?.data?.environment ?? null;
    const backendEnabled = !!(data?.enabled ?? data?.data?.enabled);
    if (!isRealtimePilotTarget(host, environment)) {
      return { enabled: false, environment, gate: `env_not_pilot:${environment}` };
    }
    return { enabled: backendEnabled, environment, gate: backendEnabled ? 'ok' : 'backend_disabled' };
  } catch {
    return { enabled: false, environment: null, gate: 'health_unreachable' };
  }
};

/**
 * Cria sessão Realtime (ephemeral secret, TTL curto).
 * @returns {Promise<{client_secret: string, calls_url: string, expires_at: number, voice: string, language: string}>}
 */
export const createRealtimeSession = async ({ voice = null, language = null, signal = undefined } = {}) => {
  const payload = {};
  if (voice) payload.voice = voice;
  if (language) payload.language = language;
  // RT2-FIX1: SEM retry genérico do axios — uma request que chegou ao backend
  // e perdeu só a resposta criaria sessões Realtime concorrentes. Falhou →
  // fallback STT; o próximo toggle do modo conversa tenta sessão nova.
  // PERF-1: o backend já faz 12s×2 (curto + 1 retry); 30s aqui cobre isso.
  const { data } = await AZURE.post('/vukz/assist/realtime/session', payload, { timeout: 30000, _skipRetry: true, signal });
  if (!data?.success || !data?.data?.client_secret) {
    throw new Error(data?.message || data?.error || 'Realtime indisponível');
  }
  return data.data;
};

/**
 * Turno de voz: texto (da tool call) → Assist canônico → { speech_text, assist_response }.
 * Retorna no MESMO shape de sendAssistMessage + speechText, para reuso do
 * pipeline de renderização do painel.
 */
export const sendRealtimeTurn = async ({
  text,
  threadId = null,
  clientMessageId = null,
  currentRoute = '',
  sport = 'swimming',
  signal = undefined, // VOICE-FINAL (V6): barge-in aborta o turno em voo
}) => {
  const payload = { text, thread_id: threadId, current_route: currentRoute, sport };
  if (clientMessageId) payload.client_message_id = clientMessageId;
  // Retry do axios é seguro: o backend deduplica por client_message_id (V15) —
  // um retry que chegasse com a mesma fala recebe replay, nunca run novo.
  const { data } = await AZURE.post('/vukz/assist/realtime/turn', payload, { timeout: 60000, signal });
  if (!data?.success) {
    throw new Error(data?.message || data?.error || 'Falha no turno de voz');
  }
  const d = data.data || {};
  return {
    threadId: d.thread_id || threadId,
    persistent: true,
    response: d.assist_response,
    meta: d.meta || {},
    speechText: d.speech_text || '',
    workspace: d.workspace,
  };
};
